import { BaseEditor, Editor, Element as SlateElement, Node, Transforms } from 'slate'
import { markdownToSlateNodes } from '../services/markdown-table'

const TABLE_TYPES = ['table', 'table-row', 'table-cell']

/**
 * Tablas en el editor.
 *
 * 1. Al pegar texto que contenga una tabla markdown (GFM) la convierte en nodos
 *    `table`/`table-row`/`table-cell` en vez de dejar el texto en crudo. Es la vía
 *    para añadir tablas desde el backoffice: se pega la tabla y ya se ve montada.
 * 2. Normaliza la estructura para que Slate no deje filas o celdas sueltas fuera de
 *    una tabla (el editor no tenía `normalizeNode` propio hasta ahora).
 */
/** `insertData` lo aporta withReact, no BaseEditor. */
type EditorWithData = BaseEditor & { insertData: (data: DataTransfer) => void }

export function withTables<T extends BaseEditor>(editor: T): T {
  const target = editor as T & EditorWithData
  const { insertData, normalizeNode } = target

  target.insertData = (data: DataTransfer) => {
    // Solo miramos el texto plano: si vienen datos de Slate o HTML enriquecido,
    // dejamos que el comportamiento por defecto haga su trabajo.
    const slateFragment = data.getData('application/x-slate-fragment')
    const text = data.getData('text/plain')

    if (!slateFragment && text) {
      const nodes = markdownToSlateNodes(text)
      if (nodes) {
        Transforms.insertNodes(editor as unknown as Editor, nodes as any)
        return
      }
    }

    insertData(data)
  }

  target.normalizeNode = (entry) => {
    const [node, path] = entry

    if (SlateElement.isElement(node)) {
      const type = (node as any).type

      // Una fila fuera de su tabla, o una celda fuera de su fila, no es
      // representable: se desenvuelve y su contenido sobrevive como bloques.
      if (type === 'table-row' || type === 'table-cell') {
        const parent = Node.parent(editor as unknown as Editor, path) as any
        const expected = type === 'table-row' ? 'table' : 'table-row'
        if (!parent || parent.type !== expected) {
          Transforms.unwrapNodes(editor as unknown as Editor, { at: path })
          return
        }
      }

      // Una tabla sin filas no pinta nada: se elimina.
      if (type === 'table' && (node as any).children?.length === 0) {
        Transforms.removeNodes(editor as unknown as Editor, { at: path })
        return
      }
    }

    normalizeNode(entry)
  }

  return editor
}

/** ¿Es un tipo de nodo de tabla? Útil para los renderers y serializadores. */
export const isTableType = (type?: string): boolean =>
  !!type && TABLE_TYPES.includes(type)
